// src/components/forms/formSteps/PhoneStep.tsx
import React from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
import { useTranslation } from "../../hooks/useTranslation";

interface PhoneStepProps {
  phone: string;
  validation: { valid: boolean; error: string };
  onChange: (field: string, value: string) => void;
  onSubmit: () => void; // Added explicit submit handler
  isSubmitting: boolean;
  isMobile: boolean;
}

const PhoneStep: React.FC<PhoneStepProps> = ({
  phone,
  validation,
  onChange,
  onSubmit,
  isSubmitting,
  isMobile,
}) => {
  // Handle keydown event - only prevent default
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const { t } = useTranslation();

  return (
    <>
      <Typography variant="h6" align="center" sx={{ mb: 3 }}>
        9. {t("form.ninthQuestion.question")}{" "}
        <span style={{ color: "red" }}>*</span>
      </Typography>

      <TextField
        fullWidth
        placeholder={t("form.ninthQuestion.placeholder")}
        value={phone}
        onChange={(e) => {
          const newValue = e.target.value;
          // Only allow digits and limit to 10 characters
          const digitsOnly = newValue.replace(/\D/g, "");
          if (digitsOnly.length <= 10) {
            onChange("phone", digitsOnly);
          }
        }}
        onKeyDown={handleKeyDown}
        error={!validation.valid}
        helperText={validation.error || t("form.ninthQuestion.placeholder")}
        inputProps={{
          maxLength: 10,
          inputMode: "numeric",
          pattern: "[0-9]*",
          className: "hyros-phone",
        }}
        sx={{ mb: 2 }}
      />

      {/* Mobile submit button placement */}
      {isMobile && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            id="SubmitForm"
            size="large"
            onClick={onSubmit}
            disabled={phone.trim() === "" || !validation.valid || isSubmitting}
            sx={{
              minWidth: "200px",
              py: 1.5,
              fontWeight: "bold",
              backgroundColor: "#ff6b35",
              "&:hover": {
                backgroundColor: "#e25a2a",
              },
            }}
          >
            {t("form.ninthQuestion.submit")}
          </Button>
        </Box>
      )}

      {/* TCPA text */}
      <div
        style={{
          textAlign: "center",
          fontSize: isMobile ? "0.6rem" : "0.8rem",
        }}
      >
        <label data-tf-element-role="consent-language">
          <div>
            {t("form.ninthQuestion.tcpa1")}{" "}
            <span data-tf-element-role="consent-advertiser-name">
              {t("form.ninthQuestion.tcpa2")}
            </span>{" "}
            {t("form.ninthQuestion.tcpa3")}{" "}
            <a href="https://www.caraccidentcounsel.com/network-of-lawyers-and-advocates/">
              {t("form.ninthQuestion.tcpa4")}
            </a>{" "}
            {t("form.ninthQuestion.tcpa5")}{" "}
            <a href="https://www.caraccidentcounsel.com/privacy-policy/">
              {t("form.ninthQuestion.tcpa6")}
            </a>
            {t("form.ninthQuestion.tcpa7")}{" "}
            <a href="https://www.caraccidentcounsel.com/terms-of-service/">
              {t("form.ninthQuestion.tcpa8")}
            </a>
            .<span className="tcpa-required">*</span>
          </div>
        </label>
      </div>

      {/* Desktop submit button placement */}
      {!isMobile && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            id="SubmitForm"
            size="large"
            onClick={onSubmit}
            disabled={phone.trim() === "" || !validation.valid || isSubmitting}
            sx={{
              minWidth: "200px",
              py: 1.5,
              fontWeight: "bold",
              backgroundColor: "#ff6b35",
              "&:hover": {
                backgroundColor: "#e25a2a",
              },
            }}
          >
            {t("form.ninthQuestion.submit")}
          </Button>
        </Box>
      )}
    </>
  );
};

export default PhoneStep;
